/**
 * BlockAuthoringPaletteShellTabs — Phase K.1.
 *
 * Tab-bar for the Block Authoring Palette shell. Three tabs match
 * AutoCAD's BPARAMETER / BACTION / "Parameter Sets" panes:
 *
 *   - "parameters"     — 10 parameter authoring tools
 *   - "actions"        — 8 action authoring tools
 *   - "parameter-sets" — 15 quick-pick combos (Phase K.4)
 *
 * The tab id is persisted in `localStorage` by the parent shell so the
 * author returns to the same tab after re-opening the palette.
 */

import { type ReactElement } from "react";

import { useT } from "../i18n";

export type BlockAuthoringPaletteTab =
  | "parameters"
  | "actions"
  | "parameter-sets";

export const BLOCK_AUTHORING_PALETTE_TABS: readonly BlockAuthoringPaletteTab[] = [
  "parameters",
  "actions",
  "parameter-sets",
];

export interface BlockAuthoringPaletteShellTabsProps {
  activeTab: BlockAuthoringPaletteTab;
  onTabChange: (next: BlockAuthoringPaletteTab) => void;
}

/**
 * Render the three tab buttons. The active button gets the
 * `is-active` class so the styles file can highlight it.
 */
export function BlockAuthoringPaletteShellTabs(
  props: BlockAuthoringPaletteShellTabsProps,
): ReactElement {
  const t = useT();
  return (
    <nav
      className="block-authoring-palette-tabs"
      role="tablist"
      aria-label={t("block_authoring.tablist.aria")}
      data-testid="block-authoring-palette-tabs"
    >
      {BLOCK_AUTHORING_PALETTE_TABS.map((tab) => {
        const labelKey = `block_authoring.tab.${tab}` as const;
        const isActive = tab === props.activeTab;
        return (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            className={
              "block-authoring-palette-tab" +
              (isActive ? " is-active" : "")
            }
            data-testid={`block-authoring-palette-tab-${tab}`}
            onClick={() => props.onTabChange(tab)}
          >
            {t(labelKey as never)}
          </button>
        );
      })}
    </nav>
  );
}
